import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import {
  MotifButton,
  MotifErrorMessage,
  MotifFormField,
  MotifIcon,
  MotifIconButton,
  MotifInput,
  MotifModal,
  MotifModalBody,
  MotifModalFooter,
  MotifModalHeader,
  MotifToast,
  MotifTooltip,
  MotifToggleSwitch,
  MotifTruncate,
} from "@ey-xd/motif-react";
import {
  IconoirHelpCircle,
  IconoirPlus,
  IconoirTrash,
} from "@ey-xd/motif-icon";
import {
  EngagementDelegation,
  EngagementProxy,
  EditEngagementPreferenceRequest,
  editEngagementDelegation,
  fetchEngagementDelegationList,
} from "../../store/slices/EngagementDelegationSlice";
import AddEngagementProxyModal, {
  AddEngagementProxyUser,
} from "../AddEngagementProxyModal";
import ConfirmationModal from "../ConfirmationModal";
import {
  getApprovalAmountError,
  hasUnsavedChanges,
  scrollToNotificationError,
} from "./helper";
import "./EditEngagementDelegationModal.scss";

type EngagementProxyDetails = EngagementProxy;

export interface EngagementDelegationChanges {
  isAutoApprove: boolean;
  autoApproveAmount: number;
  ownerNotifications: boolean;
  proxies: EngagementProxyDetails[];
}

interface EditEngagementDelegationModalProps {
  show: boolean;
  engagementData: EngagementDelegation | null;
  onClose: () => void;
  handleEngagementSaveSuccess: () => void;
}

export const renderInfoTooltip = (
  id: string,
  title: string,
  description: string,
) => (
  <MotifTooltip
    allowClick={false}
    allowHover
    contentClassName="engagement-delegation-tooltip"
    hideCloseButton
    hideArrow
    id={`${id}-tooltip`}
    trigger={
      <span className="engagement-tooltip-trigger">
        <MotifIcon iconFunction={IconoirHelpCircle} size="16" />
      </span>
    }
  >
    <div className="engagement-delegation-tooltip-content">
      <div className="engagement-delegation-tooltip-title">{title}</div>
      <div className="engagement-delegation-tooltip-description">
        {description}
      </div>
    </div>
  </MotifTooltip>
);

const EditEngagementDelegationModal = ({
  show,
  engagementData,
  onClose,
  handleEngagementSaveSuccess,
}: EditEngagementDelegationModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { name: currentUserName, emailId: currentUserEmail } = useSelector(
    (state: RootState) => state.UserProfileSlice,
  );
  const [proxies, setProxies] = useState<EngagementProxy[]>([]);
  const [isAutoApprove, setIsAutoApprove] = useState(false);
  const [approvalAmount, setApprovalAmount] = useState("");
  const [approvalAmountError, setApprovalAmountError] = useState("");
  const [notificationError, setNotificationError] = useState(false);
  const [ownerNotifications, setOwnerNotifications] = useState(false);
  const [showAddProxyModal, setShowAddProxyModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSavingEngagement, setIsSavingEngagement] = useState(false);
  const [saveEngagementError, setSaveEngagementError] = useState("");
  const [proxyNotifications, setProxyNotifications] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (!show || !engagementData) return;

    const initialProxies = engagementData.EngagementProxyDetails || [];

    setProxies(initialProxies);
    setIsAutoApprove(engagementData.IsAutoApprove.toLowerCase() === "yes");
    setApprovalAmount(String(engagementData.AutoApproveAmount || 0));
    setApprovalAmountError("");
    setNotificationError(false);
    setOwnerNotifications(Number(engagementData.PartnerSendEmail) === 1);
    setProxyNotifications(
      Object.fromEntries(
        initialProxies.map((proxy) => [
          proxy.Email.toLowerCase(),
          Number(proxy.DelegateSendEmail) === 1,
        ]),
      ),
    );
  }, [show, engagementData]);

  useEffect(() => {
    document.body.style.overflow = show ? "hidden" : "";
  }, [show, showAddProxyModal, showConfirmationModal]);

  if (!engagementData) return null;

  const ownerNotificationId = `engagement-owner-notifications-${engagementData.EngagementId}`;

  const handleModalClose = () => {
    setSaveEngagementError("");
    if (
      hasUnsavedChanges(
        engagementData,
        isAutoApprove,
        approvalAmount,
        ownerNotifications,
        proxies,
        proxyNotifications,
      )
    ) {
      setShowConfirmationModal(true);
      return;
    }
    setShowAddProxyModal(false);
    onClose();
  };

  const handleModalCancel = () => {
    setShowConfirmationModal(false);
  };

  const handleModalDiscard = () => {
    setShowConfirmationModal(false);
    setShowAddProxyModal(false);
    onClose();
  };

  const validateApprovalAmount = () => {
    const error = getApprovalAmountError(approvalAmount);
    setApprovalAmountError(error);
    return !error;
  };

  const handleApprovalAmountBlur = () => {
    validateApprovalAmount();
  };

  const handleAutoApproveToggle = () => {
    const nextIsAutoApprove = !isAutoApprove;
    setIsAutoApprove(nextIsAutoApprove);
    setApprovalAmountError("");

    if (!nextIsAutoApprove) {
      setApprovalAmount(String(engagementData.AutoApproveAmount || 0));
    }
  };

  const handleRemoveProxy = (email: string) => {
    const proxyEmail = email.toLowerCase();

    setProxies((currentProxies) =>
      currentProxies.filter(
        (proxy) => proxy.Email.toLowerCase() !== proxyEmail,
      ),
    );
    setProxyNotifications((currentNotifications) => {
      const remainingNotifications = { ...currentNotifications };
      delete remainingNotifications[proxyEmail];
      return remainingNotifications;
    });
    setNotificationError(false);
  };

  const handleAddProxy = ({ Name, Email }: AddEngagementProxyUser) => {
    setProxies((currentProxies) => {
      return [
        ...currentProxies,
        {
          Name,
          Email,
          DelegateSendEmail: 0,
          CreatorUserId: null,
          CreatorEmail: currentUserEmail || null,
          CreatorName: currentUserName || null,
        },
      ];
    });
    setShowAddProxyModal(false);
    setNotificationError(false);
  };

  const handleSave = async () => {
    if (isAutoApprove && !validateApprovalAmount()) return;
    const areAllNotificationsDisabled =
      !ownerNotifications &&
      proxies.every(
        (proxy) => !(proxyNotifications[proxy.Email.toLowerCase()] ?? false),
      );

    if (areAllNotificationsDisabled) {
      setNotificationError(true);
      scrollToNotificationError();
      return;
    }
    setNotificationError(false);

    const deletedProxies = (engagementData.EngagementProxyDetails || [])
      .filter(
        (proxy) =>
          !proxies.some(
            (activeProxy) =>
              activeProxy.Email.toLowerCase() === proxy.Email.toLowerCase(),
          ),
      )
      .map((proxy) => ({
        Name: proxy.Name,
        Email: proxy.Email,
        SendNotification: Number(proxy.DelegateSendEmail) === 1,
        IsActive: false,
      }));

    const request: EditEngagementPreferenceRequest = {
      Action: "EditEngagementPreference",
      EngagementId: engagementData.EngagementId,
      EngagementDetails: {
        EngagementPartnerNotification: ownerNotifications,
        AutoApprove: isAutoApprove,
        AutoApprovedAmount: Number(approvalAmount),
      },
      DelegateDetails: [
        ...proxies.map((proxy) => ({
          Name: proxy.Name,
          Email: proxy.Email,
          SendNotification:
            proxyNotifications[proxy.Email.toLowerCase()] ?? false,
          IsActive: true,
        })),
        ...deletedProxies,
      ],
    };
    try {
      setIsSavingEngagement(true);
      setSaveEngagementError("");
      const response = await dispatch(editEngagementDelegation(request));
      if (response.payload.Status === "success") {
        try {
          await dispatch(fetchEngagementDelegationList()).unwrap();
        } catch (fetchError) {
          console.error(
            "Failed to refresh engagement delegation list:",
            fetchError,
          );
        } finally {
          setIsSavingEngagement(false);
          handleEngagementSaveSuccess();
        }
      } else {
        setIsSavingEngagement(false);
        setSaveEngagementError(
          "An error occurred while saving details, please try again in sometime. ",
        );
        scrollToNotificationError();
      }
    } catch (error) {
      setIsSavingEngagement(false);
      console.error("Failed to edit engagement delegation:", error);
      setSaveEngagementError(
        "An error occurred while saving details, please try again in sometime. ",
      );
      scrollToNotificationError();
    }
  };

  return (
    <>
      <MotifModal
        show={show}
        onClose={handleModalClose}
        className="edit-engagement-delegation-modal"
        variant="slideout"
        focusTrapPaused={showConfirmationModal || showAddProxyModal}
      >
        <MotifModalHeader>Edit engagement delegation</MotifModalHeader>
        <MotifModalBody>
          {notificationError && (
            <MotifToast
              className="edit-engagement-delegation-notification-toast"
              onClose={() => setNotificationError(false)}
              variant="warning"
              supportingText="You can't turn off notifications for all approvers. At least one approver must have notifications enabled."
            >
              At least one approver required
            </MotifToast>
          )}
          {saveEngagementError && (
            <MotifToast
              className="edit-engagement-delegation-notification-toast"
              onClose={() => setSaveEngagementError("")}
              variant="error"
            >
              {saveEngagementError}
            </MotifToast>
          )}
          <div className="edit-engagement-delegation-content">
            <MotifFormField className="edit-engagement-delegation-engagement-field">
              <label htmlFor={`engagement-${engagementData.EngagementId}`}>
                Engagement
              </label>
              <MotifInput
                className="engagement-selector-input"
                id={`engagement-${engagementData.EngagementId}`}
                value={`${engagementData.EngagementId} | ${engagementData.EngagementName}`}
                disabled
                hideClearButton
              />
            </MotifFormField>

            <section className="engagement-details-section">
              <span className="title">Engagement details</span>
              <div className="engagement-details-card">
                <div className="engagement-detail">
                  <span className="engagement-detail-label">Engagement</span>
                  <span className="engagement-detail-value">
                    {engagementData.EngagementId}
                  </span>
                  <MotifTruncate
                    maxLines={1}
                    maxWidth={140}
                    className="engagement-detail-supporting"
                    tooltipProps={{ placement: "top" }}
                  >
                    {engagementData.EngagementName}
                  </MotifTruncate>
                </div>
                <div className="engagement-detail engagement-owner-detail">
                  <span className="engagement-detail-label">
                    Engagement owner
                  </span>
                  <MotifTruncate
                    maxLines={1}
                    maxWidth={150}
                    className="engagement-detail-value"
                    tooltipProps={{ placement: "top" }}
                  >
                    {engagementData.EngagementOwnerName}
                  </MotifTruncate>
                  <MotifTruncate
                    maxLines={1}
                    maxWidth={150}
                    className="engagement-detail-supporting"
                    tooltipProps={{ placement: "top" }}
                  >
                    {engagementData.EngagementOwnerEmail}
                  </MotifTruncate>
                </div>
                <div className="engagement-setting">
                  <div className="engagement-setting-label">
                    <span>Engagement owner notifications</span>
                    {renderInfoTooltip(
                      ownerNotificationId,
                      "Engagement owner notifications",
                      "Turn notifications on or off for the engagement owner. When turned off,no emails will be sent from Commercial Hub, including approval and extension request notifications.",
                    )}
                  </div>
                  <MotifToggleSwitch
                    id={ownerNotificationId}
                    checked={ownerNotifications}
                    onChange={() => {
                      setOwnerNotifications((value) => !value);
                      setNotificationError(false);
                    }}
                    offLabel="Off"
                    onLabel="On"
                  />
                </div>
                <div className="engagement-setting engagement-auto-approval">
                  <div className="engagement-setting-label">
                    <span>Auto approval</span>
                    {renderInfoTooltip(
                      `auto-approval-${engagementData.EngagementId}`,
                      "Auto approval",
                      `Enable auto approval to automatically approve SKU costs requests below
        the specified amount. When auto approval is enabled, you will still
        receive email notifications about auto approved SKU requests.`,
                    )}
                  </div>
                  <MotifToggleSwitch
                    id={`auto-approval-${engagementData.EngagementId}`}
                    checked={isAutoApprove}
                    onChange={handleAutoApproveToggle}
                    offLabel="Off"
                    onLabel="On"
                  />
                </div>
                <MotifFormField className="engagement-approval-amount">
                  <label
                    htmlFor={`approval-amount-${engagementData.EngagementId}`}
                  >
                    Automatically approve requests up to and including
                  </label>
                  <MotifInput
                    type="text"
                    id={`approval-amount-${engagementData.EngagementId}`}
                    value={approvalAmount}
                    placeholder=""
                    prefix={"$"}
                    suffix={"USD"}
                    disabled={!isAutoApprove}
                    hideClearButton
                    aria-invalid={Boolean(approvalAmountError)}
                    onChange={(event) => {
                      setApprovalAmount(
                        event.target.value.replace(/[^0-9.-]/g, ""),
                      );
                      setApprovalAmountError("");
                    }}
                    onBlur={handleApprovalAmountBlur}
                  />
                  {approvalAmountError && (
                    <MotifErrorMessage aria-live="assertive" role="alert">
                      {approvalAmountError}
                    </MotifErrorMessage>
                  )}
                </MotifFormField>
              </div>
            </section>

            <section className="engagement-proxies-section">
              <div className="title-wrapper">
                <span className="title">Engagement proxies</span>
                <MotifButton
                  className="add-engagement-proxy-button"
                  size="small"
                  variant="primary"
                  type="button"
                  onClick={() => setShowAddProxyModal(true)}
                >
                  Add engagement proxy
                  <MotifIcon
                    iconFunction={IconoirPlus}
                    size="20"
                    fill="#A4A3B1"
                  />
                </MotifButton>
              </div>
              <div
                className={`engagement-proxies-table ${proxies.length === 0 ? "engagement-proxies-null" : "engagement-proxies-filled"}`}
              >
                <table>
                  <colgroup>
                    <col className="engagement-proxy-column" />
                    <col className="engagement-proxy-column" />
                    <col className="engagement-proxy-notifications-column" />
                    <col className="engagement-proxy-actions-column" />
                  </colgroup>
                  <thead>
                    <tr className="engagement-proxies-header">
                      <th scope="col">
                        <div className="engagement-proxy-header-content">
                          <span>Engagement proxy</span>
                          {renderInfoTooltip(
                            `engagement-proxy-${engagementData.EngagementId}`,
                            "Engagement proxy",
                            `Add a person to act on behalf of the engagement owner. Engagement
        proxies can set approval thresholds, approve requests, and manage other
        engagement proxies.`,
                          )}
                        </div>
                      </th>
                      <th scope="col">
                        <span>Added by</span>
                      </th>
                      <th scope="col">
                        <div className="engagement-proxy-header-content">
                          <span>Notifications</span>
                          {renderInfoTooltip(
                            `proxy-notifications-${engagementData.EngagementId}`,
                            "Notifications",
                            `Turn notifications on or off for this user. When turned off, the user will not receive emails from Commercial Hub, including approval and extension request notifications.`,
                          )}
                        </div>
                      </th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proxies.length === 0 ? (
                      <tr className="engagement-proxy-row engagement-proxy-empty-row">
                        <td colSpan={4}>
                          <div className="engagement-proxies-empty-state">
                            <span className="engagement-proxies-empty-title">
                              No engagement proxies yet
                            </span>
                            <span className="engagement-proxies-empty-supporting">
                              Add engagement proxies to see them displayed here.
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <>
                        {proxies.map((proxy) => {
                          const proxyEmail = proxy.Email.toLowerCase();
                          const notifications =
                            proxyNotifications[proxyEmail] ?? false;
                          return (
                            <tr
                              className="engagement-proxy-row"
                              key={proxyEmail}
                            >
                              <td>
                                <div className="engagement-proxy-person">
                                  <MotifTruncate
                                    maxLines={1}
                                    maxWidth={140}
                                    tooltipProps={{ placement: "top" }}
                                  >
                                    {proxy.Name || "-"}
                                  </MotifTruncate>
                                  <MotifTruncate
                                    maxLines={1}
                                    maxWidth={140}
                                    className="engagement-detail-supporting"
                                    tooltipProps={{ placement: "top" }}
                                  >
                                    {proxy.Email || "-"}
                                  </MotifTruncate>
                                </div>
                              </td>
                              <td>
                                <div className="engagement-proxy-person">
                                  <MotifTruncate
                                    maxLines={1}
                                    maxWidth={140}
                                    tooltipProps={{ placement: "top" }}
                                  >
                                    {proxy.CreatorName || "-"}
                                  </MotifTruncate>
                                  <MotifTruncate
                                    maxLines={1}
                                    maxWidth={140}
                                    className="engagement-detail-supporting"
                                    tooltipProps={{ placement: "top" }}
                                  >
                                    {proxy.CreatorEmail || "-"}
                                  </MotifTruncate>
                                </div>
                              </td>
                              <td>
                                <MotifToggleSwitch
                                  id={`proxy-notifications-${engagementData.EngagementId}-${proxyEmail}`}
                                  checked={notifications}
                                  onChange={() => {
                                    setProxyNotifications((current) => ({
                                      ...current,
                                      [proxyEmail]: !notifications,
                                    }));
                                    setNotificationError(false);
                                  }}
                                  offLabel="Off"
                                  onLabel="On"
                                />
                              </td>
                              <td>
                                <div className="engagement-proxy-actions">
                                  <MotifIconButton
                                    aria-label={`Remove ${proxy.Name}`}
                                    onClick={() =>
                                      handleRemoveProxy(proxy.Email)
                                    }
                                  >
                                    <MotifIcon
                                      iconFunction={IconoirTrash}
                                      size="20"
                                      fill="#A4A3B1"
                                      className="action-button"
                                    />
                                  </MotifIconButton>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </MotifModalBody>
        <MotifModalFooter>
          <MotifButton
            size="medium"
            variant="primary-alt"
            type="button"
            onClick={handleModalClose}
          >
            Cancel
          </MotifButton>
          <MotifButton
            size="medium"
            variant="primary"
            type="button"
            onClick={handleSave}
            disabled={isSavingEngagement}
          >
            Save changes
          </MotifButton>
        </MotifModalFooter>
      </MotifModal>
      {showAddProxyModal && (
        <AddEngagementProxyModal
          show={showAddProxyModal}
          onClose={() => setShowAddProxyModal(false)}
          focusTrapPaused={showConfirmationModal}
          onAdd={handleAddProxy}
          existingEmails={[
            engagementData.EngagementOwnerEmail,
            ...proxies.map((proxy) => proxy.Email),
          ]}
        />
      )}
      {showConfirmationModal && (
        <ConfirmationModal
          isVisible={showConfirmationModal}
          setIsVisible={setShowConfirmationModal}
          header="Discard changes?"
          message="If you leave now, your progress will be lost."
          cancelLabel="Cancel"
          confirmLabel="Discard changes"
          cancelButtonHandler={handleModalCancel}
          confirmButtonHandler={handleModalDiscard}
        />
      )}
    </>
  );
};

export default EditEngagementDelegationModal;
